import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Buildings, Lock } from "@phosphor-icons/react";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = user?.role === "titolare";
  const [form, setForm] = useState({ name: "", address: "", vat: "" });
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ password_set: false });
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");

  const loadPay = useCallback(() => {
    api.get("/documents/payslip-status").then((r) => setPay(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({ name: r.data.name || "", address: r.data.address || "", vat: r.data.vat || "" }))
      .catch((e) => toast.error(errMsg(e)));
    loadPay();
  }, [loadPay]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", form);
      toast.success("Impostazioni salvate: l'intestazione sarà usata in login e report");
      window.dispatchEvent(new Event("company-updated"));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const savePayslipPwd = async () => {
    if (pwd !== pwd2) { toast.error("Le due password non coincidono"); return; }
    setBusy(true);
    try {
      await api.post("/documents/payslip-password", { password: pwd });
      toast.success("Password buste paga aggiornata");
      setPwd(""); setPwd2(""); loadPay();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="settings-page" className="space-y-3 max-w-xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Impostazioni</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isOwner ? "Password di accesso alle buste paga" : "Intestazione della ditta e accesso alle buste paga"}
        </p>
      </div>

      {isAdmin && (
        <div className="rounded-sm border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Buildings size={17} className="text-primary" /> Dati della ditta
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ragione sociale</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-9 rounded-sm mt-1" placeholder="Es. Rossi Impianti S.r.l." data-testid="settings-name" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Indirizzo</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="h-9 rounded-sm mt-1" placeholder="Via, città" data-testid="settings-address" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Partita IVA</Label>
            <Input value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })}
              className="h-9 rounded-sm mt-1" data-testid="settings-vat" />
          </div>
          <Button className="rounded-sm font-semibold" onClick={save} disabled={busy || !form.name.trim()} data-testid="settings-save">
            Salva impostazioni
          </Button>
        </div>
      )}

      <div className="rounded-sm border border-border bg-card p-4 space-y-3" data-testid="payslip-password-card">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Lock size={17} className="text-primary" /> Password buste paga
        </div>
        <p className="text-xs text-muted-foreground">
          Le buste paga sono visibili solo all'Amministratore, che deve inserire questa password per
          aprirle. I Responsabili non hanno accesso alle buste paga.
          {" "}
          <span className={pay.password_set ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}
            data-testid="payslip-password-state">
            {pay.password_set ? "Password impostata." : "Password non ancora impostata."}
          </span>
        </p>
        {isOwner ? (
          <>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {pay.password_set ? "Nuova password" : "Password"}
              </Label>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="h-9 rounded-sm mt-1" placeholder="Almeno 4 caratteri" data-testid="settings-payslip-pwd" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ripeti password</Label>
              <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                className="h-9 rounded-sm mt-1" data-testid="settings-payslip-pwd2" />
            </div>
            <Button className="rounded-sm font-semibold" onClick={savePayslipPwd}
              disabled={busy || pwd.trim().length < 4} data-testid="settings-payslip-save">
              {pay.password_set ? "Modifica password" : "Imposta password"}
            </Button>
          </>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="payslip-password-owner-note">
            Solo il Titolare può impostare o modificare questa password.
          </p>
        )}
      </div>
    </div>
  );
}
